

import React from "react";
import { useTranslations } from "next-intl";
import { IconArrowBackUp, IconRotateClockwise2 } from "@tabler/icons-react";

/**
 * What the history row hands the action column (see `checkInActionValue` in
 * CheckInRows). `undoOf`: this row IS an undo — the worker wrote it to restore
 * the state from before the row it points at. `undoneBy`: this row WAS taken
 * back by the undo row it points at. A Java build that predates the fields
 * sends neither, and other producers still pass the bare action string.
 *
 * `title` is the composed tooltip line ("Checked Out · Reverted"): `TableCell`
 * derives its overflow tooltip from string values only, and the marks below
 * are icon-only, so without it the narrow column had no way to show the
 * words. Composed by the producer (which has `t`), read by `TableCell`.
 */
export type CheckInActionValue = {
  action: string;
  undoOf?: string | null;
  undoneBy?: string | null;
  title?: string;
};

type TCCCheckInLogActionProps = {
  value: string | CheckInActionValue;
};

/** The `checkIn` namespace translator, as the producer and the chip get it. */
export type CheckInTranslator = ReturnType<typeof useTranslations<"checkIn">>;

const ACTION_STYLES: Record<string, string> = {
  CHECK_IN: "user-icon-font-4 user-icon-bg-4 outline-user-icon-4",
  CHECK_OUT: "user-icon-font-2 user-icon-bg-2 outline-user-icon-2",
  APPOINTMENT_CHECK_IN: "user-icon-font-1 user-icon-bg-1 outline-user-icon-1",
  SEATING_CHECK_IN: "user-icon-font-6 user-icon-bg-6 outline-user-icon-6",
  SESSION_CHECK_IN: "user-icon-font-9 user-icon-bg-9 outline-user-icon-9",
  FIELD_UPDATED: "user-icon-font-7 user-icon-bg-7 outline-user-icon-7",
  SCAN_REJECTED: "user-icon-font-11 user-icon-bg-11 outline-user-icon-11",
  // A repair, not a door event — muted on purpose, so a corrected row does not
  // read louder than the scan it corrects.
  CHECK_IN_RESET: "user-icon-font-17 user-icon-bg-17 outline-user-icon-17",
};

const ACTION_LABEL_KEYS: Record<string, string> = {
  CHECK_IN: "actionCheckIn",
  CHECK_OUT: "actionCheckOut",
  APPOINTMENT_CHECK_IN: "actionAppointmentCheckIn",
  SEATING_CHECK_IN: "actionSeatingCheckIn",
  SESSION_CHECK_IN: "actionSessionCheckIn",
  FIELD_UPDATED: "actionFieldUpdated",
  SCAN_REJECTED: "actionScanRejected",
  CHECK_IN_RESET: "actionCheckInReset",
};

/** The base chip's words for an action; an unknown action shows itself. */
export function checkInActionLabel(t: CheckInTranslator, action: string): string {
  const key = ACTION_LABEL_KEYS[action];
  return key ? (t as (k: string) => string)(key) : action;
}

/**
 * The one line the narrow cell's tooltip shows: base label, then the mark's
 * words — "Checked Out · Reverted". Same words the mark announces, so hover
 * and screen reader agree.
 */
export function checkInActionCellTitle(
  t: CheckInTranslator,
  value: CheckInActionValue,
): string {
  const parts = [checkInActionLabel(t, value.action)];
  const tt = t as (k: string) => string;
  if (value.undoOf) parts.push(tt("actionUndone"));
  if (value.undoneBy) parts.push(tt("actionWasUndone"));
  return parts.join(" · ");
}

// The undo marks are annotations on the base chip, not actions of their own:
// neutral chrome, so the row reads as one corrected event and not as two.
//
// ICON-ONLY on purpose. The column is 150 px by default and the cell wrapper
// is nowrap + hidden, so a second text chip ("Ausgecheckt · Rückgängig
// gemacht") was clipped mid-word with no tooltip. The words live in the
// mark's accessible name and native title, and in the cell's composed
// tooltip title (see `checkInActionCellTitle`).
const UNDO_MARK_STYLE = "font-tertiary surface-tertiary outline-quaternary";

const CHIP = "inline-flex items-center px-3 py-1 rounded-xl border body-3-medium";
const MARK = "inline-flex items-center px-1.5 py-1 rounded-xl border";

// A value object is what defeats `React.memo`'s shallow compare here — the same
// trade the CTA column makes, and the history page re-renders its rows on data
// changes only, so the cost is one chip per row per fetch.
export const TCCCheckInLogAction: React.FC<TCCCheckInLogActionProps> =
  React.memo(function TCCCheckInLogAction({ value }) {
    const t = useTranslations("checkIn");

    const action = typeof value === "string" ? value : (value?.action ?? "");
    const undoOf = typeof value === "string" ? undefined : value?.undoOf;
    const undoneBy = typeof value === "string" ? undefined : value?.undoneBy;

    const style = ACTION_STYLES[action] ?? ACTION_STYLES.CHECK_OUT;
    const label = checkInActionLabel(t, action);

    const base = <span className={`${CHIP} ${style}`}>{label}</span>;
    if (!undoOf && !undoneBy) return base;

    const undoneLabel = t("actionUndone");
    const wasUndoneLabel = t("actionWasUndone");

    // The visually-hidden text is for the CELL's tooltip, not for assistive
    // tech: `OverflowTooltip` builds its bubble from the wrapper's rendered
    // `textContent` (and then keeps that text for the native title), so an
    // icon-only mark left the bubble saying just "Checked Out" — the mark's
    // word unreachable exactly when the column was too narrow to show it.
    // With the hidden " · Reverted" the rendered text equals the composed
    // cell title. Inside `role="img"` the child text is presentational, so a
    // screen reader still announces the `aria-label` once, not twice.
    return (
      <span className="inline-flex items-center gap-1">
        {base}
        {undoOf ? (
          <span
            role="img"
            aria-label={undoneLabel}
            title={undoneLabel}
            className={`${MARK} ${UNDO_MARK_STYLE}`}
            data-testid="checkin-action-undo"
          >
            <IconArrowBackUp size={14} aria-hidden="true" />
            <span className="sr-only"> · {undoneLabel}</span>
          </span>
        ) : null}
        {undoneBy ? (
          <span
            role="img"
            aria-label={wasUndoneLabel}
            title={wasUndoneLabel}
            className={`${MARK} ${UNDO_MARK_STYLE}`}
            data-testid="checkin-action-undone"
          >
            <IconRotateClockwise2 size={14} aria-hidden="true" />
            <span className="sr-only"> · {wasUndoneLabel}</span>
          </span>
        ) : null}
      </span>
    );
  });
